import React from 'react';
import clsx from 'clsx';
import styles from '../../card-listing/Card.module.scss';
import contentListStyles from './ContentListCard.module.scss';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import { CardImage } from '../__helpers__/CardImageWrapper';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { ContentType } from 'lib/services/models/types';
import { SearchRecord } from 'lib/services/models/search-response';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { BaseContentResponse } from 'lib/services/models/content-response';
import { useContentGridSettings } from 'lib/hooks/useSiteSettings';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import {
  getImageSettingsByContentType,
  normalizeStandardImageUrl,
  ContentOverride,
} from 'components/authorable/shared/card-listing/CardListingShared';
import { buildContentResultPayload } from 'components/helpers/Analytics/ContentListCard/Content';
import { pushToDataLayer } from 'components/helpers/Global/Analytics/DataLayer';

export type CardAlignment = 'LeftAlign' | 'CenterAlign';

export interface ContentListCardProps {
  contentImage: React.ReactNode | null;
  imageLabel?: React.ReactNode | null;
  showLabel?: boolean;
  eyebrow?: React.ReactNode | null;
  title?: React.ReactNode | null;
  description?: React.ReactNode | null;
  headline?: React.ReactNode | null;
  showNumberedList?: boolean;
  results?: SearchRecord[];
  contentOverrides?: Record<string, ContentOverride>;
  fallbackImageUrl?: string;
  gridAspectRatio: ImageAspectRatio;
  alignment?: CardAlignment;
  additionalCssClassName?: string;
}

const preferOverrideString = (overrideVal?: string, fallbackVal?: string): string | undefined =>
  overrideVal && overrideVal.trim().length > 0 ? overrideVal : fallbackVal;

export const ContentListCardComponent: React.FC<ContentListCardProps> = (props) => {
  const contentItems = Array.isArray(props.results) ? props.results : [];
  const hasContentList = contentItems.length > 0;
  const alignment: CardAlignment = props.alignment ?? 'LeftAlign';

  const contentGridSettings = useContentGridSettings();
  const siteDefaultAspectRatio =
    getEnumValue<ImageAspectRatio>(contentGridSettings?.fields?.defaultCardImageAspectRatio) ??
    undefined;

  const contentData = contentItems[0];
  const contentType: ContentType | undefined =
    (contentData?.ContentType as ContentType | undefined) ?? undefined;

  const { aspectRatio: typeAspectRatioRaw, forceWhiteBackground } = getImageSettingsByContentType(
    contentGridSettings,
    contentType
  );

  const typeAspectRatio =
    typeof typeAspectRatioRaw === 'object'
      ? (getEnumValue<ImageAspectRatio>(typeAspectRatioRaw) ?? undefined)
      : (typeAspectRatioRaw as ImageAspectRatio | undefined);
  const imageRatio: ImageAspectRatio =
    typeAspectRatio ??
    siteDefaultAspectRatio ??
    props.gridAspectRatio ??
    ('3:2' as ImageAspectRatio);

  const language = useGlobalData().locale;

  const getOverrideForRecord = (searchData?: SearchRecord): ContentOverride | undefined => {
    if (!searchData || !props.contentOverrides) return undefined;
    return props.contentOverrides[searchData.ContentId];
  };

  const primaryOverride = getOverrideForRecord(contentData);
  const cardUrl =
    preferOverrideString(primaryOverride?.url, contentData?.ItemUrl || contentData?.PageUrl) ?? '#';
  const normalizedImageUrl =
    normalizeStandardImageUrl(primaryOverride?.image, contentData?.PageUrl) ||
    normalizeStandardImageUrl(contentData?.StandardImageUrl, contentData?.PageUrl) ||
    normalizeStandardImageUrl(props.fallbackImageUrl, contentData?.PageUrl);
  const contentTypeValue = (contentData?.ContentType as string | undefined) ?? 'Content';
  const pushContentResult = (searchData: SearchRecord) => {
    const payload = buildContentResultPayload({
      content: {
        ContentId: searchData.ContentId ?? '',
        Title: searchData.Title ?? '',
      } as Partial<BaseContentResponse>,
      isNumberedList: props.showNumberedList ?? false,
      language,
      contentType: contentTypeValue,
      recordType: searchData.RecordType ?? 'Content',
    });
    pushToDataLayer(payload);
  };

  const ListTag = (props.showNumberedList ? 'ol' : 'ul') as 'ol' | 'ul';

  return (
    <>
      {props.headline && <h2 className={contentListStyles.cardHeading}>{props.headline}</h2>}
      <div
        className={clsx(
          styles.card,
          contentListStyles.cardContentList,
          hasContentList && contentListStyles.hasContentList,
          props.additionalCssClassName,
          {
            [styles.card_Center]: alignment === 'CenterAlign',
          }
        )}
      >
        <div className={clsx(contentListStyles.cardImageWrapper)}>
          <CtaLink
            url={cardUrl}
            className={clsx(styles.cardImageWrapper, styles.cardImageWrapperGeneral)}
            ctaStyle="none"
            onClick={() => {
              if (contentData) pushContentResult(contentData);
            }}
          >
            {props.showLabel !== false && props.imageLabel && (
              <div className={styles.cardLabelWrapper}>
                <div className={styles.cardLabel}>dsds{props.imageLabel}</div>
              </div>
            )}

            {props.contentImage ? (
              <div
                className={clsx(
                  styles.cardImageWrapper,
                  styles[`image--${(props.gridAspectRatio ?? '3:2').replace(':', '-')}`]
                )}
              >
                <div className={clsx(styles.cardImage)}>{props.contentImage}</div>
              </div>
            ) : (
              normalizedImageUrl && (
                <CardImage
                  imageSrc={normalizedImageUrl}
                  gridAspectRatio={props.gridAspectRatio ?? '3:2'}
                  aspectRatio={imageRatio}
                  forceWhiteBackground={forceWhiteBackground}
                />
              )
            )}
          </CtaLink>
        </div>
        {(props.eyebrow || props.title || props.description) && (
          <div className={clsx(contentListStyles.cardBody)}>
            {props.eyebrow && (
              <p className={contentListStyles.cardGeneralEyebrow}>{props.eyebrow}</p>
            )}

            {props.title ? <h3 className={clsx(styles.cardBodyTitle)}>{props.title}</h3> : null}

            {props.description ? (
              <div className={clsx(contentListStyles.cardBodyDescription)}>{props.description}</div>
            ) : null}
          </div>
        )}
        {contentItems.length > 0 && (
          <div className={contentListStyles.cardContentListSection}>
            <div className={contentListStyles.cardContentListItems}>
              <ListTag>
                {contentItems.map((content, index) => {
                  const ovr = getOverrideForRecord(content);
                  const itemTitle = preferOverrideString(ovr?.title, content.Title);
                  const itemUrl = preferOverrideString(
                    ovr?.url,
                    content.ItemUrl || content.PageUrl
                  );
                  return itemUrl && itemTitle ? (
                    <li key={content.ContentId ?? `${content.PageUrl}-${index}`}>
                      <CtaLink
                        url={String(itemUrl)}
                        className={contentListStyles.contentCardLink}
                        ctaStyle={'link'}
                        ctaComponentClass="componentTheme-cards-on-surface-text-primary"
                        text={itemTitle}
                        onClick={() => pushContentResult(content)}
                      />
                    </li>
                  ) : null;
                })}
              </ListTag>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default ContentListCardComponent;
